import React, { useEffect, useRef, useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, Button, Input, Textarea } from '@heroui/react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { Check, X, Lightning, Buildings, StackSimple } from '@phosphor-icons/react';
import { useBuildingsAndUnits } from 'hooks/useBuildingsAndUnits';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (issue?: any) => void;
};

export const QuickAddInsuranceIssueModal: React.FC<Props> = ({ isOpen, onClose, onCreated }) => {
  const { session } = useSession();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState<any>(null);
  const [selectedUnit, setSelectedUnit] = useState<any>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const { buildings, units, getUnitsForBuilding } = useBuildingsAndUnits();

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const reset = () => {
    setTitle('');
    setDescription('');
    setSelectedBuilding(null);
    setSelectedUnit(null);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!title.trim()) return;
    if (!selectedBuilding) return;
    setIsSubmitting(true);
    try {
      const body: any = {
        title: title.trim(),
        description: description.trim() || title.trim(),
        buildingId: selectedBuilding.id,
        ...(selectedUnit && { unitId: selectedUnit.id }),
      };
      const res = await fetchWithAuth(
        `${API_URL}/insurance/file`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
        session
      );
      if (!res.ok) throw new Error('Failed');
      const issue = await res.json();
      onCreated?.(issue);
      reset();
    } catch {
      // handled by caller toasts typically
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="2xl"
      scrollBehavior="inside"
      classNames={{ base: 'max-w-2xl', backdrop: 'bg-black/50', wrapper: 'items-center justify-center' }}
      isDismissable={!isSubmitting}
      hideCloseButton={isSubmitting}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2 pb-2">
          <Lightning size={24} className="text-moss-primary" />
          <span className="text-xl font-semibold text-neutral-primary">Snel verzekeringsdossier</span>
        </ModalHeader>
        <ModalBody className="pb-6">
          <div className="space-y-4">
            <Input
              ref={titleInputRef}
              label="Titel"
              placeholder="Korte titel..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              variant="bordered"
              classNames={{ input: 'text-neutral-primary', inputWrapper: 'border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary' }}
              isDisabled={isSubmitting}
              isRequired
            />
            <Textarea
              label="Beschrijving (optioneel)"
              placeholder="Korte beschrijving..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              variant="bordered"
              minRows={3}
              maxRows={6}
              classNames={{ input: 'text-neutral-primary', inputWrapper: 'border-neutral-primary/20 hover:border-moss-primary focus-within:border-moss-primary' }}
              isDisabled={isSubmitting}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-primary mb-2">
                  <Buildings size={16} className="inline mr-1" />
                  Gebouw <span className="text-red-600">*</span>
                </label>
                <select
                  value={selectedBuilding?.id || ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    const b = buildings.find((bb: any) => bb.id === id);
                    setSelectedBuilding(b || null);
                    if (selectedUnit && selectedUnit.building?.id !== id) setSelectedUnit(null);
                  }}
                  className="w-full min-h-[40px] px-3 py-2 rounded-lg border bg-white border-neutral-primary/20 focus:border-moss-primary"
                  disabled={isSubmitting}
                >
                  <option value="">Selecteer gebouw...</option>
                  {buildings.map((b: any) => (
                    <option key={b.id} value={b.id}>{b.id}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-primary mb-2">
                  <StackSimple size={16} className="inline mr-1" />
                  Unit
                </label>
                <select
                  value={selectedUnit?.id || ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    const us = selectedBuilding ? getUnitsForBuilding(selectedBuilding.id) : units;
                    const u = (us || []).find((uu: any) => uu.id === id) || null;
                    setSelectedUnit(u);
                  }}
                  className="w-full min-h-[40px] px-3 py-2 rounded-lg border bg-white border-neutral-primary/20 focus:border-moss-primary"
                  disabled={isSubmitting || !selectedBuilding}
                >
                  <option value="">Selecteer unit (optioneel)...</option>
                  {(selectedBuilding ? getUnitsForBuilding(selectedBuilding.id) : units).map((u: any) => (
                    <option key={u.id} value={u.id}>{u.id}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t border-neutral-primary/10">
              <div className="text-xs text-neutral-secondary-mid">💡 Tip: Druk Ctrl+Enter om snel op te slaan</div>
              <div className="flex items-center gap-3">
                <Button variant="light" onClick={handleClose} isDisabled={isSubmitting} startContent={<X size={16} />}>Annuleren</Button>
                <Button onClick={handleSubmit} isLoading={isSubmitting} className="bg-moss-primary text-white hover:bg-moss-primary/90" startContent={!isSubmitting ? <Check size={16} /> : undefined}>
                  {isSubmitting ? 'Aanmaken...' : 'Dossier aanmaken'}
                </Button>
              </div>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default QuickAddInsuranceIssueModal;

